import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router'
import toInteger from 'lodash/toInteger'
import { PERMISSION_ENTITY_INVOICE } from '@klear/constants'
import { KlearDate } from '@klear/date'
import type { InvoiceEventFormValues } from '@klear/invoice/wire.client'
import { Card } from '@klear/ui/components/card'
import { InvoiceEventForm } from '@klear/order-event-ui/event-handlers/InvoiceEventForm'
import { useEventFormBootstrap } from '@klear/order-event-ui/event-shared/use-event-form-bootstrap'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import type { ReceivableStepCtx } from '@/app/receivable/workflow/ReceivableWizardContext'

export default function InvoiceStep({ ctx }: WizardStepComponentProps<ReceivableStepCtx>) {
  const { traderId: traderIdParam } = useParams<{ traderId?: string }>()
  const traderId = toInteger(traderIdParam)
  const { form, patchFormAndGoNext, goPrev } = ctx

  const { bootstrap, isLoading } = useEventFormBootstrap({
    eventType: PERMISSION_ENTITY_INVOICE,
    editEventId: form.invoiceEventId ?? undefined,
    parentEventId: form.purchaseOrderEventId ?? undefined,
    traderId
  })

  const invoiceBootstrap = useMemo(() => {
    if (bootstrap == null || bootstrap.editEventId != null) return bootstrap
    const formValues: InvoiceEventFormValues = {
      ...(bootstrap.formValues as InvoiceEventFormValues),
      endDate: KlearDate.today()
    }
    return { ...bootstrap, formValues }
  }, [bootstrap])

  const handleSaved = useCallback(
    (savedEventId?: number) => {
      if (savedEventId == null) return
      patchFormAndGoNext((draft) => {
        draft.invoiceEventId = savedEventId
      })
    },
    [patchFormAndGoNext]
  )

  if (form.orderId == null) {
    return <div className="text-sm text-muted-foreground">Select an order first.</div>
  }
  if (isLoading || invoiceBootstrap == null) {
    return <div className="text-sm text-muted-foreground">Loading invoice…</div>
  }

  return (
    <div className="space-y-6 w-full max-w-2xl">
      <Card className="p-6 space-y-4">
        <InvoiceEventForm
          bootstrap={invoiceBootstrap}
          orderId={form.orderId}
          traderId={traderId}
          onSuccess={handleSaved}
          onCancel={goPrev}
        />
      </Card>
    </div>
  )
}
